import { useState, useEffect, useRef, useCallback } from 'react';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import Button from 'components/Buttons/Button';
import AddSubscriptionTierModal from 'components/Home/PromotionPlans/AddSubscriptionTierModal';
import SmartAdvisorModal from 'components/Home/PromotionPlans/SmartAdvisorModal';
import { useAppSelector } from 'hooks/useRedux';
import { RootState } from 'store';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { http } from 'middleware/apiMiddleware';
import {
  getSubscriptions,
  updateSubscription,
  subscriptionExamples,
  subscriptionAdvisorUsageCheck,
  subscriptionAdvisorHistory,
} from 'constants/apiUrls';
import SubscriptionTierCard from 'components/Home/PromotionPlans/SubscriptionTierCard';
import { Loader } from 'components/Common/Loader';
import { toast } from 'react-hot-toast';
import { Sparkle20Regular } from '@fluentui/react-icons';
import subscriotion from 'assets/susbcription.gif';
import BusinessHeader from 'components/Home/BusinessHeader';
import CloseCircleIcon from 'assets/icons/CloseCircleIcon';

const SubscriptionTiers = () => {
  const [activeTab, setActiveTab] = useState<'my-subscriptions' | 'create-subscription'>(
    'create-subscription',
  );
  const [showAddTierModal, setShowAddTierModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exampleSubscriptionsByCategory, setExampleSubscriptionsByCategory] = useState<
    Map<string, any[]>
  >(new Map());
  const [exampleCategories, setExampleCategories] = useState<{ id: string; label: string }[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [createSubscriptionOption, setCreateSubscriptionOption] = useState<
    'smart-advisor' | 'create-blank' | 'examples' | null
  >(null);
  const [isSmartAdvisorExpanded, setIsSmartAdvisorExpanded] = useState(true);
  const [suggestionHistory, setSuggestionHistory] = useState<
    Array<{ timestamp: Date; suggestions: any[] }>
  >([]);
  const [expandedSuggestionIndexes, setExpandedSuggestionIndexes] = useState<Set<number>>(
    new Set([0]),
  );
  const [usageData, setUsageData] = useState<{
    maxLimit: number;
    usageCount: number;
    disableUsage: boolean;
  }>({
    maxLimit: 10,
    usageCount: 0,
    disableUsage: false,
  });
  const closeModalHandlerRef = useRef<(() => void) | null>(null);

  const handleCloseAttempt = useCallback((handler: () => void) => {
    closeModalHandlerRef.current = handler;
  }, []);

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Toggle suggestion accordion expansion
  const toggleSuggestionAccordion = (index: number) => {
    setExpandedSuggestionIndexes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const [selectedBusiness] = useLocalStorage('selectedBusiness', null);
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const business: BusinessType = businessState?.business || selectedBusiness || null;

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);

      if (!business?.uuid) return;

      const { data, error } = await http.get(getSubscriptions(business.uuid));

      if (error) {
        toast.error('Failed to fetch subscriptions');
        return;
      }

      if (data) {
        setSubscriptions(data);
      }
    } catch (error) {
      toast.error('Unexpected error occurred while fetching subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestionHistory = async () => {
    try {
      if (!business?.uuid) return;

      const { data, error } = await http.get(subscriptionAdvisorHistory(business.uuid));

      if (error) {
        return;
      }

      if (data && Array.isArray(data)) {
        // Group suggestions by rounded timestamp (group by minute to handle millisecond differences)
        const groupedByTimestamp = new Map<string, any[]>();

        data.forEach((item: any) => {
          // Round timestamp to the nearest minute to group generations together
          const timestamp = new Date(item.createdAt || item.timestamp);
          const roundedKey = `${timestamp.getFullYear()}-${timestamp.getMonth()}-${timestamp.getDate()}-${timestamp.getHours()}-${timestamp.getMinutes()}`;

          if (!groupedByTimestamp.has(roundedKey)) {
            groupedByTimestamp.set(roundedKey, []);
          }

          // Transform the item to match our suggestion format
          const items = [];

          // Add primary item
          if (item.cleanResponse?.primaryItem?.name) {
            items.push({
              itemName: item.cleanResponse.primaryItem.name,
              quantity: item.cleanResponse.primaryItem.quantity,
              limit: item.cleanResponse.primaryItem.limit,
              limitPeriod: `PER_${item.cleanResponse.primaryItem.period?.toUpperCase() || 'DAY'}`,
            });
          }

          // Add secondary item if exists
          if (item.cleanResponse?.secondaryItem?.name) {
            items.push({
              itemName: item.cleanResponse.secondaryItem.name,
              quantity: item.cleanResponse.secondaryItem.quantity,
              limit: item.cleanResponse.secondaryItem.limit,
              limitPeriod: `PER_${item.cleanResponse.secondaryItem.period?.toUpperCase() || 'DAY'}`,
            });
          }

          const suggestion = {
            title: item.cleanResponse?.tagLine || item.title || '',
            description: item.cleanResponse?.description || item.description || '',
            price: item.cleanResponse?.price || item.price || 0,
            cycle: item.cleanResponse?.cycle || item.cycle || 'month',
            maxSubscribers: item.cleanResponse?.maxSubscribers || item.maxSubscribers || 0,
            items: items,
            rules: item.cleanResponse?.storeRules
              ? item.cleanResponse.storeRules.split('\n')
              : item.rules || [],
            justification: item.cleanResponse?.justification || item.justification || '',
          };

          groupedByTimestamp.get(roundedKey)?.push({
            timestamp: timestamp,
            suggestion: suggestion,
          });
        });

        // Convert to array format and sort by timestamp (latest first)
        const transformedHistory = Array.from(groupedByTimestamp.values())
          .map((group) => ({
            timestamp: group[0].timestamp, // Use the first item's timestamp for the group
            suggestions: group.map((item) => item.suggestion),
          }))
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        setSuggestionHistory(transformedHistory);
        // Expand the first (latest) accordion by default
        if (transformedHistory.length > 0) {
          setExpandedSuggestionIndexes(new Set([0]));
        }
      }
    } catch (error) {
      // Silent fail - history is optional
    }
  };

  const checkUsage = async () => {
    try {
      if (!business?.uuid) return;

      const { data, error } = await http.get(subscriptionAdvisorUsageCheck(business.uuid));

      if (error) {
        return;
      }

      if (data) {
        setUsageData(data);
      }
    } catch (error) {
      // Silent fail - usage check is optional
    }
  };

  const handleEditSubscription = (subscription: any) => {
    setSelectedSubscription(subscription);
    setShowAddTierModal(true);
  };

  const handleDuplicateExample = (subscription: any) => {
    // Remove uuid and other fields that shouldn't be duplicated
    const { uuid, subscribersCount, currentSubscribers, category, ...duplicateData } =
      subscription;
    setSelectedSubscription(duplicateData);
    setActiveTab('my-subscriptions');
    setShowAddTierModal(true);
  };

  const handleToggleSubscription = async (subscription: any, newState: boolean) => {
    try {
      if (!business?.uuid) return;

      const { data, error } = await http.patch(
        updateSubscription(business.uuid, subscription.uuid),
        { is_active: newState },
      );

      if (error) {
        toast.error('Failed to update subscription status');
        return;
      }

      if (data) {
        toast.success(`Subscription ${newState ? 'activated' : 'deactivated'} successfully`);
        // Refresh subscriptions list
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error('Unexpected error occurred while updating subscription');
    }
  };

  const fetchExamples = async () => {
    try {
      setLoadingExamples(true);

      const { data, error } = await http.get(subscriptionExamples());

      if (error) {
        return;
      }

      if (data?.examples) {
        // Group examples by category
        const categoryMap = new Map<string, any[]>();

        data.examples.forEach((example: any) => {
          if (!categoryMap.has(example.category)) {
            categoryMap.set(example.category, []);
          }
          categoryMap.get(example.category)?.push({
            ...example,
            uuid: `example-${Math.random().toString(36).substr(2, 9)}`,
            subscribersCount: 0,
            currentSubscribers: 0,
            isActive: true,
          });
        });

        // Create category list
        const categories = Array.from(categoryMap.keys()).map((category) => ({
          id: category,
          label: category,
        }));

        // Store the organized data directly - no need to flatten and filter later
        setExampleCategories(categories);
        setExampleSubscriptionsByCategory(categoryMap);

        // Expand first category by default
        if (categories.length > 0) {
          setExpandedCategories(new Set([categories[0].id]));
        }
      }
    } catch (error) {
      setExampleCategories([]);
      setExampleSubscriptionsByCategory(new Map());
    } finally {
      setLoadingExamples(false);
    }
  };

  // Fetch subscriptions on mount to determine if tab should be enabled/disabled
  useEffect(() => {
    if (business?.uuid) {
      fetchSubscriptions();
    }
  }, [business?.uuid]);

  // Fetch suggestion history when switching to create-subscription tab
  useEffect(() => {
    if (activeTab === 'create-subscription') {
      // Check usage when switching to create-subscription tab
      if (business?.uuid && createSubscriptionOption === 'smart-advisor') {
        checkUsage();
      }
      // Fetch suggestion history
      if (business?.uuid) {
        fetchSuggestionHistory();
      }
    }
  }, [activeTab, createSubscriptionOption]);

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Business Header - Sticky */}
      <div className="px-8 pt-6">
        <BusinessHeader selectedBusiness={business} />
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto bg-white">
        <div className="px-8 pb-8 flex flex-col gap-5">
          {/* 1. Benefits Card */}
          <div className="w-full max-w-[1134px] mb-4 md:mb-8 border border-gray200 rounded-lg bg-white">
            {/* Header */}
            <div className="h-[48px] md:h-[59px] rounded-t-lg px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
              <h2 className="font-poppins font-semibold text-lg leading-none text-darkBlack">
                Grow with Subscriptions
              </h2>
            </div>

            {/* Content */}
            <div className="p-4 md:p-8">
              <p className="mb-6 w-full max-w-[1094px] font-poppins font-medium text-sm leading-relaxed md:leading-none text-darkBlue">
                Subscriptions have been a widely successful business model for a variety of online
                companies - it's time we bring the same power to local businesses
              </p>

              <ul className="space-y-2 text-base text-darkBlue">
                <li className="flex items-center">
                  <span className="mr-2 text-darkBlack">•</span>
                  <span className="font-poppins font-normal text-sm leading-none text-darkBlack">
                    Generate prepaid and recurring revenue
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2 text-darkBlack">•</span>
                  <span className="font-poppins font-normal text-sm leading-none text-darkBlack">
                    Target slower times of the day or the week
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2 text-darkBlack">•</span>
                  <span className="font-poppins font-normal text-sm leading-none text-darkBlack">
                    Lock in customer intent upfront
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2 text-darkBlack">•</span>
                  <span className="font-poppins font-normal text-sm leading-none text-darkBlack">
                    Bring foot traffic through the door
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2 text-darkBlack">•</span>
                  <span className="font-poppins font-normal text-sm leading-none text-darkBlack">
                    Transform the relationship with frequent shoppers
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2 text-darkBlack">•</span>
                  <span className="font-poppins font-normal text-sm leading-none text-darkBlack">
                    Better inventory planning based on demand
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2 text-royalBlue">•</span>
                  <span className="font-poppins font-normal text-sm leading-none text-royalBlue">
                  No cost to setup, only pay when customers subscribe (5% platform fee)
                  </span>
                </li>
              </ul>

              {/* Download Merchant App & Tutorial buttons */}
              <div className="flex items-center gap-3 md:gap-4 mt-6">
                <a
                  href={process.env.REACT_APP_MERCHANT_APP_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 md:gap-2 h-[37px] px-3 md:px-4 py-2 rounded-full font-poppins font-medium text-xs md:text-sm leading-none text-center text-white bg-[#0102CE] hover:opacity-90 transition-opacity whitespace-nowrap"
                  style={{ letterSpacing: '0%' }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0"
                  >
                    <path
                      d="M14 9V13C14 13.1326 13.9473 13.2598 13.8536 13.3536C13.7598 13.4473 13.6326 13.5 13.5 13.5H2.5C2.36739 13.5 2.24021 13.4473 2.14645 13.3536C2.05268 13.2598 2 13.1326 2 13V9C2 8.86739 2.05268 8.74021 2.14645 8.64645C2.24021 8.55268 2.36739 8.5 2.5 8.5C2.63261 8.5 2.75979 8.55268 2.85355 8.64645C2.94732 8.74021 3 8.86739 3 9V12.5H13V9C13 8.86739 13.0527 8.74021 13.1464 8.64645C13.2402 8.55268 13.3674 8.5 13.5 8.5C13.6326 8.5 13.7598 8.55268 13.8536 8.64645C13.9473 8.74021 14 8.86739 14 9ZM7.64625 9.35375C7.69269 9.40024 7.74783 9.43712 7.80853 9.46228C7.86923 9.48744 7.93429 9.50039 8 9.50039C8.06571 9.50039 8.13077 9.48744 8.19147 9.46228C8.25217 9.43712 8.30731 9.40024 8.35375 9.35375L10.8538 6.85375C10.9002 6.8073 10.9371 6.75214 10.9622 6.69145C10.9873 6.63075 11.0003 6.5657 11.0003 6.5C11.0003 6.4343 10.9873 6.36925 10.9622 6.30855C10.9371 6.24786 10.9002 6.1927 10.8538 6.14625C10.8073 6.09979 10.7521 6.06294 10.6914 6.0378C10.6308 6.01266 10.5657 5.99972 10.5 5.99972C10.4343 5.99972 10.3692 6.01266 10.3086 6.0378C10.2479 6.06294 10.1927 6.09979 10.1462 6.14625L8.5 7.79313V2C8.5 1.86739 8.44732 1.74021 8.35355 1.64645C8.25979 1.55268 8.13261 1.5 8 1.5C7.86739 1.5 7.74021 1.55268 7.64645 1.64645C7.55268 1.74021 7.5 1.86739 7.5 2V7.79313L5.85375 6.14625C5.75993 6.05243 5.63268 5.99972 5.5 5.99972C5.36732 5.99972 5.24007 6.05243 5.14625 6.14625C5.05243 6.24007 4.99972 6.36732 4.99972 6.5C4.99972 6.63268 5.05243 6.75993 5.14625 6.85375L7.64625 9.35375Z"
                      fill="white"
                    />
                  </svg>
                  Download Merchant App
                </a>
                <a
                  href={process.env.REACT_APP_MERCHANT_APP_TUTORIAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 h-[37px] px-5 md:px-6 py-2 rounded-full font-poppins font-medium text-xs md:text-sm leading-none text-center text-[#0102CE] bg-white border border-[#0102CE] hover:opacity-90 transition-opacity whitespace-nowrap"
                  style={{ letterSpacing: '0%' }}
                >
                  Tutorial
                </a>
              </div>
            </div>
          </div>

          {/* 2. Tabs Section */}

          <div className="w-full max-w-[1134px] mb-4 md:mb-8">
            <div className="flex w-full md:w-auto">
              <Button
                onClick={() => {
                  if (subscriptions.length > 0) {
                    setActiveTab('my-subscriptions');
                  }
                }}
                disabled={subscriptions.length === 0}
                label={
                  <span className="flex items-center gap-2.5">
                    <span className="font-poppins font-medium text-sm leading-none text-center">
                      My Subscriptions
                    </span>
                    <span className="font-poppins font-semibold text-xs flex items-center justify-center w-[18px] h-[18px] rounded bg-lavenderLight text-royalBlue">
                      {subscriptions.length}
                    </span>
                  </span>
                }
                variant={activeTab === 'my-subscriptions' ? 'primary' : 'secondary'}
                customClass={`!rounded-l-lg !rounded-r-none flex-1 md:!flex-none md:!w-[180px] !h-[40px] md:!h-[45px] !mb-0 !me-0 !px-2 md:!px-6 !whitespace-nowrap ${
                  activeTab === 'my-subscriptions'
                    ? '!text-white !bg-primary'
                    : '!bg-white !border !border-gray300 !text-greyText'
                }`}
              />
              <Button
                onClick={() => {
                  setActiveTab('create-subscription');
                  setSelectedSubscription(null);
                  setCreateSubscriptionOption(null);
                }}
                label={
                  <span className="font-poppins font-medium text-sm leading-none">
                    <span className="inline md:hidden">Create</span>
                    <span className="hidden md:inline">Create Subscription</span>
                  </span>
                }
                variant={activeTab === 'create-subscription' ? 'primary' : 'secondary'}
                customClass={`!rounded-r-lg !rounded-l-none !border-l-0 flex-1 md:!flex-none md:!w-[200px] !h-[40px] md:!h-[45px] !mb-0 !me-0 !px-2 md:!px-6 !whitespace-nowrap ${
                  activeTab === 'create-subscription'
                    ? '!text-white !bg-primary'
                    : '!bg-white !border !border-gray300 !text-greyText'
                }`}
              />
            </div>
          </div>

          {/* 3. Content Section based on active tab */}
          {activeTab === 'my-subscriptions' ? (
            <>
              {/* Show Add Subscription Form if modal is open */}
              {showAddTierModal ? (
                <div className="w-full max-w-[1134px] bg-white rounded-lg border border-gray200">
                  <div className="flex items-center justify-between h-[48px] md:h-[59px] gap-4 md:gap-[92px] px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
                    <h2 className="flex-1 font-poppins font-semibold text-base md:text-lg leading-none text-textDark">
                      {selectedSubscription?.uuid
                        ? 'Edit Subscription Tier'
                        : 'Create Subscription'}
                    </h2>
                    <button
                      onClick={() => {
                        if (closeModalHandlerRef.current) {
                          closeModalHandlerRef.current();
                        } else {
                          setShowAddTierModal(false);
                          setSelectedSubscription(null);
                        }
                      }}
                      className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center flex-shrink-0 text-gray500 hover:text-gray700 transition-colors"
                    >
                      <CloseCircleIcon />
                    </button>
                  </div>
                  <div className="px-4 md:px-8 py-4 md:py-6">
                    <p className="mb-4 md:mb-6 font-poppins font-medium text-xs md:text-sm leading-none ">
                      {selectedSubscription?.uuid
                        ? 'Update your subscription tier details and save changes.'
                        : 'Add a new subscription offer.'}
                    </p>
                    <AddSubscriptionTierModal
                      onClose={() => {
                        setShowAddTierModal(false);
                        setSelectedSubscription(null);
                        closeModalHandlerRef.current = null;
                      }}
                      onSubmit={(_data: any) => {
                        fetchSubscriptions();
                        setSelectedSubscription(null);
                        setShowAddTierModal(false);
                        closeModalHandlerRef.current = null;
                      }}
                      onCloseAttempt={handleCloseAttempt}
                      businessUuid={business?.uuid}
                      initialData={selectedSubscription}
                    />
                  </div>
                </div>
              ) : loading ? (
                <div className="flex justify-center items-center py-16">
                  <Loader size={8} color={'darkBlue'} />
                </div>
              ) : subscriptions.length > 0 ? (
                <>
                  <div className="w-full max-w-[1134px] border border-gray200 rounded-lg bg-white">
                    {/* Header */}
                    <div className="h-[48px] md:h-[59px] rounded-t-lg px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
                      <h2 className="font-poppins font-semibold text-base md:text-lg leading-none text-textDark">
                        My Subscriptions
                      </h2>
                    </div>

                    <div className="p-4 md:p-8">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-[37px]">
                        {subscriptions.map((subscription) => (
                          <SubscriptionTierCard
                            key={subscription.uuid}
                            subscription={subscription}
                            onEdit={handleEditSubscription}
                            onToggle={handleToggleSubscription}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full max-w-[1134px] border border-gray200 rounded-lg bg-white">
                  {/* Header */}
                  <div className="h-[48px] md:h-[59px] rounded-t-lg px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
                    <h2 className="font-poppins font-semibold text-base md:text-lg leading-none text-textDark">
                      My Subscriptions
                    </h2>
                  </div>

                  <div className="flex flex-col items-center justify-center py-8 md:py-16 px-4 md:px-8">
                    {/* Empty State Illustration - GIF Image */}
                    <div className="mb-6 md:mb-8">
                      <img
                        src={subscriotion}
                        alt="No Subscription Plans"
                        className="w-[330px] h-[261px]"
                      />
                    </div>

                    <h3 className="text-lg md:text-2xl font-semibold text-darkBlue mb-3 md:mb-4 text-center">
                      No Subscription Plans Yet
                    </h3>
                    <p className="text-sm md:text-base text-darkBlue text-center max-w-full md:max-w-4xl px-2">
                      Get started faster by letting the{' '}
                      <span className="text-primary font-semibold">Smart Advisor</span> build
                      personalized subscription plans for you. You can review, adjust, or create
                      your own manually anytime.
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : activeTab === 'create-subscription' ? (
            <>
              {!createSubscriptionOption ? (
                /* Landing Page - Default view for Create Subscription */
                <div className="w-full max-w-[1134px] border border-gray200 rounded-lg bg-white overflow-hidden">
                  {/* Header */}
                  <div className="h-[48px] md:h-[59px] rounded-t-lg px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
                    <h2 className="font-poppins font-semibold text-base md:text-lg leading-none text-textDark">
                      Create Subscription
                    </h2>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col items-center justify-center px-4 md:px-8 py-6 md:py-8 min-h-[400px]">
                    {/* Illustration */}
                    <div className="mb-4 md:mb-6">
                      <img
                        src={subscriotion}
                        alt="Create Subscription"
                        className="w-[250px] md:w-[330px] h-auto"
                      />
                    </div>

                    {/* Description Text */}
                    <div className="mb-6 px-4 w-full max-w-[1014px] flex items-center justify-center">
                      <p className="font-poppins font-normal text-sm leading-relaxed text-darkBlue text-center">
                        Get started faster by letting the{' '}
                        <span className="font-poppins font-semibold text-sm">Smart Advisor</span>{' '}
                        build personalized subscription plans for you. You can review, adjust, or
                        create your own manually anytime.
                      </p>
                    </div>

                    {/* Three Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center w-full px-4">
                      <button
                        onClick={() => {
                          setCreateSubscriptionOption('smart-advisor');
                          setIsSmartAdvisorExpanded(true);
                          if (business?.uuid) {
                            checkUsage();
                          }
                        }}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-white border-0 cursor-pointer transition-all duration-200 hover:opacity-90 bg-violet rounded-full h-[37px] sm:min-w-[160px] font-poppins font-medium text-sm"
                      >
                        <Sparkle20Regular className="w-5 h-5 flex-shrink-0" />
                        <span className="leading-none text-center">Smart advisor</span>
                      </button>
                      <button
                        onClick={() => {
                          if (exampleSubscriptionsByCategory.size === 0) {
                            fetchExamples();
                          }
                          setCreateSubscriptionOption('examples');
                        }}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-white border border-royalBlue cursor-pointer transition-all duration-200 hover:bg-royalBlue hover:bg-opacity-5 rounded-full h-[37px] sm:min-w-[100px] font-poppins font-medium text-sm text-royalBlue"
                      >
                        <span className="leading-none text-center">Examples</span>
                      </button>
                      <button
                        onClick={() => {
                          setCreateSubscriptionOption('create-blank');
                          setSelectedSubscription(null);
                        }}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-lightPurple text-royalBlue rounded-full border-0 cursor-pointer transition-all duration-200 hover:opacity-90 h-[37px] sm:min-w-[215px] font-poppins font-medium text-sm"
                      >
                        <span className="leading-none text-center">Create blank subscription</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : createSubscriptionOption === 'smart-advisor' ? (
                <>
                  <div className="w-full max-w-[1134px] bg-white rounded-lg border border-gray200">
                    <div className="w-full px-4 md:px-6 py-4 md:py-5 border-b border-gray200">
                      {/* Top row: title + close at right */}
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => setIsSmartAdvisorExpanded(!isSmartAdvisorExpanded)}
                          className="flex items-center gap-2 md:gap-3 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <div className="text-gray600 hover:text-gray800 transition-colors shrink-0">
                            {isSmartAdvisorExpanded ? (
                              <IoChevronUp className="w-5 h-5 md:w-6 md:h-6" />
                            ) : (
                              <IoChevronDown className="w-5 h-5 md:w-6 md:h-6" />
                            )}
                          </div>
                          <h2
                            className="font-poppins font-semibold leading-none text-sm md:text-lg shrink-0"
                            style={{
                              fontWeight: 600,
                              lineHeight: '100%',
                              letterSpacing: '0%',
                              color: '#0B1215',
                              verticalAlign: 'middle',
                            }}
                          >
                            Smart Advisor
                          </h2>
                          {/* Desktop: badge inline with title */}
                          <span
                            className="hidden md:inline-flex font-poppins font-medium leading-none whitespace-nowrap text-xs items-center shrink-0"
                            style={{
                              height: '30px',
                              paddingTop: '6px',
                              paddingRight: '12px',
                              paddingBottom: '6px',
                              paddingLeft: '12px',
                              borderRadius: '4px',
                              backgroundColor: '#F5F5FF',
                              lineHeight: '100%',
                              letterSpacing: '0%',
                              color: '#7F00FF',
                            }}
                          >
                            {usageData?.usageCount ?? 0}/{usageData?.maxLimit ?? 10} Smart Advisor
                            Generations Used Today
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setCreateSubscriptionOption(null);
                            setIsSmartAdvisorExpanded(false);
                          }}
                          className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center text-gray500 hover:text-gray700 transition-colors shrink-0"
                          aria-label="Close Smart Advisor"
                        >
                          <CloseCircleIcon />
                        </button>
                      </div>
                      {/* Mobile: usage badge below Smart Advisor, bigger */}
                      <div className="mt-3 md:hidden w-full">
                        <span
                          className="inline-flex font-poppins font-medium text-sm items-center justify-center w-full py-2.5 px-3 rounded whitespace-nowrap"
                          style={{
                            borderRadius: '6px',
                            backgroundColor: '#F5F5FF',
                            lineHeight: '100%',
                            letterSpacing: '0%',
                            color: '#7F00FF',
                          }}
                        >
                          {usageData?.usageCount ?? 0}/{usageData?.maxLimit ?? 10} Smart Advisor
                          Generations Used Today
                        </span>
                      </div>
                    </div>

                    {isSmartAdvisorExpanded && (
                      <div className="px-4 md:px-6 py-5 md:py-6">
                        <SmartAdvisorModal
                          onSubmit={(_suggestions: any) => {
                            // Refetch suggestion history to get the latest from API
                            if (business?.uuid) {
                              fetchSuggestionHistory();
                            }
                            // Collapse the form
                            setIsSmartAdvisorExpanded(false);
                            // Expand the first (latest) accordion by default
                            setExpandedSuggestionIndexes(new Set([0]));
                          }}
                          businessUuid={business?.uuid}
                          onUsageUpdate={(usage: any) => {
                            setUsageData(usage);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Promotion Suggestions - Multiple Accordions (Latest First) */}
                  {suggestionHistory.length > 0 && (
                    <div className="w-full max-w-[1134px] mt-6 space-y-4">
                      {suggestionHistory.map(
                        (entry: { timestamp: Date; suggestions: any[] }, entryIndex: number) => {
                          const isExpanded = expandedSuggestionIndexes.has(entryIndex);

                          return (
                            <div
                              key={entryIndex}
                              className="bg-white rounded-lg border border-gray200"
                            >
                              {/* Accordion Header */}
                              <button
                                onClick={() => toggleSuggestionAccordion(entryIndex)}
                                className="w-full px-4 md:px-6 py-4 md:py-5 flex items-center hover:bg-gray50 transition-colors text-left"
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="text-gray600">
                                    {isExpanded ? (
                                      <IoChevronUp className="w-5 h-5 md:w-6 md:h-6" />
                                    ) : (
                                      <IoChevronDown className="w-5 h-5 md:w-6 md:h-6" />
                                    )}
                                  </div>
                                  <h2 className="font-poppins font-semibold text-lg md:text-xl leading-tight text-black">
                                    Subscription Suggestions
                                  </h2>
                                  <span
                                    className="font-poppins font-normal text-sm text-gray500 flex items-center justify-center whitespace-nowrap"
                                    style={{
                                      height: '30px',
                                      paddingTop: '6px',
                                      paddingRight: '12px',
                                      paddingBottom: '6px',
                                      paddingLeft: '12px',
                                      borderRadius: '4px',
                                      background: '#F6F5FC', // light lavender
                                    }}
                                  >
                                    {`${entry.timestamp.getMonth() + 1}/${entry.timestamp.getDate()}/${entry.timestamp.getFullYear()}`}{' '}
                                    •{' '}
                                    {entry.timestamp
                                      .toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true,
                                      })
                                      .toUpperCase()}
                                  </span>
                                </div>
                              </button>

                              {/* Accordion Content - All Suggestions for this generation */}
                              {isExpanded && (
                                <>
                                  {/* Latest accordion - Show with justification */}
                                  {entryIndex === 0 ? (
                                    <div className="px-4 md:px-6 py-4 md:py-6 border-t border-gray200 space-y-8">
                                      {entry.suggestions.map(
                                        (suggestion: any, suggestionIndex: number) => (
                                          <div
                                            key={suggestionIndex}
                                            className="flex flex-col lg:flex-row gap-5 w-full max-w-[1094px]"
                                          >
                                            {/* Left side - Subscription Card */}
                                            <div className="w-full lg:w-[537px] lg:h-[415px]">
                                              <div className="bg-lavenderBg border-2 border-lavenderBorder rounded-2xl overflow-hidden">
                                                {/* Header */}
                                                <div className="px-5 py-3 bg-lavenderBorder border-b-2 border-lavenderBorder">
                                                  <h4 className="font-poppins font-semibold text-white text-xs uppercase tracking-wide">
                                                    {suggestion.maxSubscribers
                                                      ? `${suggestion.maxSubscribers} Subscriptions`
                                                      : 'Recommended Subscription'}
                                                  </h4>
                                                </div>

                                                {/* Content */}
                                                <div className="p-4">
                                                  <div className="bg-white rounded-xl p-4 border border-lavenderBorder">
                                                    <h3 className="font-poppins font-semibold text-lavenderText text-lg capitalize mb-2">
                                                      {suggestion.title}
                                                    </h3>
                                                    <p className="text-gray500 font-poppins text-xs mb-4">
                                                      {suggestion.description}
                                                    </p>
                                                    <div className="mb-4">
                                                      <span className="font-poppins font-normal text-lavenderText text-3xl">
                                                        ${suggestion.price}
                                                      </span>
                                                      <span className="font-poppins font-normal text-lavenderText text-sm">
                                                        / {suggestion.cycle}
                                                      </span>
                                                    </div>
                                                    <div className="space-y-2">
                                                      {suggestion.items?.map(
                                                        (item: any, idx: number) => (
                                                          <div
                                                            key={idx}
                                                            className="bg-lightPinkBg text-royalPink font-poppins font-medium text-xs px-4 py-2 rounded-lg inline-block"
                                                          >
                                                            {item.quantity} X {item.itemName} Limit{' '}
                                                            {item.limit} Per{' '}
                                                            {item.limitPeriod.replace('PER_', '')}
                                                          </div>
                                                        ),
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* Store Rules */}
                                                  {suggestion.rules &&
                                                    suggestion.rules.length > 0 && (
                                                      <div className="mt-4">
                                                        <h4 className="font-poppins font-medium text-sm text-gray600 mb-2">
                                                          Store Rules
                                                        </h4>
                                                        <ul className="space-y-1.5">
                                                          {suggestion.rules.map(
                                                            (rule: string, idx: number) => (
                                                              <li
                                                                key={idx}
                                                                className="font-poppins text-sm text-black flex items-start"
                                                              >
                                                                <span className="mr-2">•</span>
                                                                <span>{rule}</span>
                                                              </li>
                                                            ),
                                                          )}
                                                        </ul>
                                                      </div>
                                                    )}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Right side - Justification */}
                                            <div className="w-full lg:w-[537px] lg:h-[367px] flex flex-col gap-5 p-4 md:p-5">
                                              <h4 className="font-poppins text-gray500 text-base font-normal leading-none">
                                                Justification :
                                              </h4>
                                              <p className="font-poppins font-normal text-sm leading-none capitalize text-grayMedium flex-1">
                                                {suggestion.justification}
                                              </p>
                                              <div className="flex justify-start">
                                                <Button
                                                  onClick={() => {
                                                    setSelectedSubscription(suggestion);
                                                    setCreateSubscriptionOption('create-blank');
                                                  }}
                                                  label="Copy this suggestion"
                                                  customClass="!w-auto !px-8 !py-3 !mb-0 !rounded-full"
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  ) : (
                                    /* Previous accordions - Show in grid layout like Examples */
                                    <div className="px-4 md:px-6 py-4 md:py-6 border-t border-gray200">
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                                        {entry.suggestions.map(
                                          (suggestion: any, suggestionIndex: number) => (
                                            <div
                                              key={suggestionIndex}
                                              className="bg-lavenderBg border-2 border-lavenderBorder rounded-2xl overflow-hidden flex flex-col"
                                              style={{
                                                width: '340px',
                                                height: '473px',
                                              }}
                                            >
                                              {/* Header */}
                                              <div className="px-5 py-3 bg-lavenderBorder border-b-2 border-lavenderBorder">
                                                <h4 className="font-poppins font-semibold text-white text-xs uppercase tracking-wide">
                                                  {suggestion.maxSubscribers
                                                    ? `${suggestion.maxSubscribers} Subscriptions`
                                                    : '0 Subscriptions'}
                                                </h4>
                                              </div>

                                              {/* Content - with overflow handling */}
                                              <div className="p-4 flex-1 flex flex-col overflow-hidden">
                                                <div
                                                  className="bg-white rounded-xl p-3 border border-lavenderBorder mb-3 overflow-y-auto flex-shrink-0"
                                                  style={{ maxHeight: '280px' }}
                                                >
                                                  <h3 className="font-poppins font-semibold text-lavenderText text-base capitalize mb-1.5">
                                                    {suggestion.title}
                                                  </h3>
                                                  <p className="text-gray500 font-poppins text-xs mb-3 line-clamp-3">
                                                    {suggestion.description}
                                                  </p>
                                                  <div className="mb-3">
                                                    <span className="font-poppins font-normal text-lavenderText text-2xl">
                                                      ${suggestion.price}
                                                    </span>
                                                    <span className="font-poppins font-normal text-lavenderText text-sm">
                                                      / {suggestion.cycle}
                                                    </span>
                                                  </div>
                                                  <div className="space-y-1.5">
                                                    {suggestion.items?.map(
                                                      (item: any, idx: number) => (
                                                        <div
                                                          key={idx}
                                                          className="bg-lightPinkBg text-royalPink font-poppins font-medium text-xs px-3 py-1.5 rounded-lg inline-block w-full"
                                                        >
                                                          {item.quantity} X {item.itemName} Limit{' '}
                                                          {item.limit} Per{' '}
                                                          {item.limitPeriod.replace('PER_', '')}
                                                        </div>
                                                      ),
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Store Rules */}
                                                {suggestion.rules &&
                                                  suggestion.rules.length > 0 && (
                                                    <div className="mb-3 flex-shrink-0">
                                                      <h4 className="font-poppins font-medium text-sm text-gray600 mb-1.5">
                                                        Store Rules
                                                      </h4>
                                                      <ul className="space-y-1">
                                                        {suggestion.rules.map(
                                                          (rule: string, idx: number) => (
                                                            <li
                                                              key={idx}
                                                              className="font-poppins text-xs text-black flex items-start"
                                                            >
                                                              <span className="mr-2">•</span>
                                                              <span className="line-clamp-2">
                                                                {rule}
                                                              </span>
                                                            </li>
                                                          ),
                                                        )}
                                                      </ul>
                                                    </div>
                                                  )}

                                                {/* Use this suggestion button - Always at bottom */}
                                                <div className="mt-auto pt-2 flex justify-start flex-shrink-0">
                                                  <button
                                                    onClick={() => {
                                                      setSelectedSubscription(suggestion);
                                                      setCreateSubscriptionOption('create-blank');
                                                    }}
                                                    className="font-poppins text-white bg-royalBlue hover:bg-royalBlueDark transition-colors whitespace-nowrap flex items-center justify-center"
                                                    style={{
                                                      fontWeight: 500,
                                                      fontSize: '12px',
                                                      lineHeight: '100%',
                                                      letterSpacing: '0%',
                                                      textAlign: 'center',
                                                      height: '34px',
                                                      paddingTop: '8px',
                                                      paddingRight: '20px',
                                                      paddingBottom: '8px',
                                                      paddingLeft: '20px',
                                                      borderRadius: '40px',
                                                      borderWidth: '1px',
                                                      borderColor: 'transparent',
                                                      gap: '8px',
                                                    }}
                                                  >
                                                    Use this example
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}
                </>
              ) : createSubscriptionOption === 'create-blank' ? (
                <div className="w-full max-w-[1134px] bg-white rounded-lg border border-gray200">
                  <div className="flex items-center justify-between h-[48px] md:h-[59px] gap-4 md:gap-[92px] px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
                    <h2 className="flex-1 font-poppins font-semibold text-base md:text-lg leading-none text-textDark">
                      Create blank Subscription
                    </h2>
                    <button
                      onClick={() => {
                        if (closeModalHandlerRef.current) {
                          closeModalHandlerRef.current();
                        } else {
                          setCreateSubscriptionOption(null);
                          setSelectedSubscription(null);
                        }
                      }}
                      className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center flex-shrink-0 text-gray500 hover:text-gray700 transition-colors"
                    >
                      <CloseCircleIcon />
                    </button>
                  </div>
                  <div className="px-4 md:px-8 py-4 md:py-6">
                    <p className="mb-4 md:mb-6 font-poppins font-medium text-xs md:text-sm leading-none">
                      Add a new subscription offer.
                    </p>
                    <AddSubscriptionTierModal
                      onClose={() => {
                        setCreateSubscriptionOption(null);
                        setSelectedSubscription(null);
                        closeModalHandlerRef.current = null;
                      }}
                      onSubmit={(_data: any) => {
                        fetchSubscriptions();
                        setSelectedSubscription(null);
                        setCreateSubscriptionOption(null);
                        closeModalHandlerRef.current = null;
                      }}
                      onCloseAttempt={handleCloseAttempt}
                      businessUuid={business?.uuid}
                      initialData={selectedSubscription}
                    />
                  </div>
                </div>
              ) : createSubscriptionOption === 'examples' ? (
                <div className="w-full max-w-[1134px] border border-gray200 rounded-lg bg-white">
                  {/* Header */}
                  <div className="flex items-center justify-between h-[48px] md:h-[59px] gap-4 md:gap-[92px] rounded-t-lg px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
                    <h2
                      className="flex-1 font-poppins leading-none"
                      style={{
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        color: '#0B1215',
                        verticalAlign: 'middle',
                      }}
                    >
                      Examples
                    </h2>
                    <button
                      onClick={() => {
                        setCreateSubscriptionOption(null);
                      }}
                      className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center flex-shrink-0 text-gray500 hover:text-gray700 transition-colors"
                    >
                      <CloseCircleIcon />
                    </button>
                  </div>

                  <div className="px-4 md:px-8">
                    {loadingExamples ? (
                      <div className="flex justify-center items-center py-16">
                        <Loader size={8} color={'darkBlue'} />
                      </div>
                    ) : (
                      <div className="space-y-0">
                        {/* Accordion Categories */}
                        {exampleCategories.map((category, index) => {
                          const isExpanded = expandedCategories.has(category.id);
                          const categorySubscriptions =
                            exampleSubscriptionsByCategory.get(category.id) || [];
                          const isLast = index === exampleCategories.length - 1;

                          return (
                            <div key={category.id}>
                              {/* Accordion Header */}
                              <button
                                onClick={() => toggleCategory(category.id)}
                                className="w-full flex items-center justify-between py-3 md:py-4 border-b border-gray200"
                              >
                                <h3
                                  className="font-poppins leading-none text-left"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '100%',
                                    letterSpacing: '0%',
                                    color: '#0C3073', // navyDark
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  {category.label}
                                </h3>
                                {isExpanded ? (
                                  <IoChevronUp className="w-5 h-5 md:w-6 md:h-6 text-gray600 flex-shrink-0" />
                                ) : (
                                  <IoChevronDown className="w-5 h-5 md:w-6 md:h-6 text-gray600 flex-shrink-0" />
                                )}
                              </button>

                              {/* Accordion Content */}
                              {isExpanded && (
                                <div
                                  className={`py-4 md:py-6 ${!isLast ? 'border-b border-gray200' : ''}`}
                                >
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-[37px]">
                                    {categorySubscriptions.map((subscription) => (
                                      <SubscriptionTierCard
                                        key={subscription.uuid}
                                        subscription={subscription}
                                        onEdit={handleDuplicateExample}
                                        isExample={true}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTiers;
